import * as React from 'react';
import { cn } from '@/lib/utils';
import { AITextHelper } from '@/components/ai/AITextHelper';
import { VoiceInputButton } from '@/components/voice/VoiceInputButton';

export interface AIInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onValueChange?: (value: string) => void;
  aiContext?: string;
  aiFieldLabel?: string;
  aiDisabled?: boolean;
  voiceInput?: boolean;
}

const AIInput = React.forwardRef<HTMLInputElement, AIInputProps>(
  (
    {
      className,
      type,
      value,
      onChange,
      onValueChange,
      aiContext,
      aiFieldLabel,
      aiDisabled,
      disabled,
      voiceInput = true,
      ...props
    },
    ref
  ) => {
    const innerRef = React.useRef<HTMLInputElement | null>(null);
    const mergedRef = React.useCallback(
      (node: HTMLInputElement | null) => {
        innerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
      },
      [ref]
    );

    const handleValueChange = (newValue: string) => {
      if (onValueChange) {
        onValueChange(newValue);
      } else {
        const event = {
          target: { value: newValue },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(event);
      }
    };

    const showVoice = voiceInput && disabled !== true;

    return (
      <div className="relative flex w-full min-w-0 items-center">
        <input
          type={type}
          value={value}
          onChange={onChange}
          disabled={disabled}
          className={cn(
            'flex h-9 w-full rounded-md border border-border bg-card px-3 py-1 pr-14 text-sm shadow-sm transition-colors',
            'placeholder:text-muted',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'dark:text-foreground',
            className
          )}
          ref={mergedRef}
          {...props}
        />
        {showVoice && (
          <VoiceInputButton
            inputRef={innerRef}
            value={value}
            onValueChange={handleValueChange}
            disabled={disabled}
            placement="inline"
            pairedWithAi
          />
        )}
        {/* Full-width overlay so KI-Vorschlag popovers use the input row width, not the sparkles button width */}
        <div className="pointer-events-none absolute inset-0 min-h-9">
          <AITextHelper
            value={value}
            onValueChange={handleValueChange}
            context={aiContext}
            fieldLabel={aiFieldLabel}
            disabled={aiDisabled ?? disabled}
            placement="inline"
            className="h-full w-full min-w-0"
          />
        </div>
      </div>
    );
  }
);
AIInput.displayName = 'AIInput';

export { AIInput };
